import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";

import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IFilter } from "@formant/core/model/IFilter";
import { Uuid } from "@formant/core/model/Uuid";
import {
    defaultStoreId,
    useFormantClientContext,
} from "@formant/rectangle-sdk";
import { EditorStoreContextProvider } from "@formant/views-editor";

import { track } from "../../analytics";
import {
    appStore,
    deviceDetailsStore,
    deviceStore,
    viewStore,
} from "../../stores";
import { NotFound } from "../NotFound";
import { StatelessViewDevice } from "./StatelessViewDevice";
import { ViewDeviceState } from "./ViewDeviceState";

function isNotFound<T>(it: T | false): it is false {
    return it === false;
}

function isLoading<T>(it: T | undefined): it is undefined {
    return it === undefined;
}

interface IConnectedViewDeviceProps {
    id: Uuid;
    viewId?: Uuid | undefined;
    className?: string;
}

export const ConnectedViewDevice = observer(function ConnectedViewDevice(
    props: IConnectedViewDeviceProps,
) {
    const { id: deviceId, viewId } = props;

    const { setGlobalFilter } = useFormantClientContext();
    const [viewDeviceState] = useState(() => new ViewDeviceState());
    const [device, setDevice] = useState<IDevice | false | undefined>();
    const [deviceDetails, setDeviceDetails] = useState<
        IDeviceDetails | false | undefined
    >();

    useEffect(() => {
        viewDeviceState.viewId = viewId;
    }, [viewId]);

    useEffect(() => {
        appStore.saveRecentDeviceId(deviceId);
        track("View Device", { "Device ID": deviceId });

        if (deviceId) {
            void deviceStore.getAsync(deviceId).then(data => {
                setDevice(data);
            });
            void deviceDetailsStore.listAsync().then(() => {
                setDeviceDetails(deviceDetailsStore.get(deviceId));
            });
        }
    }, [deviceId]);

    const onChangeFilter = (filter: IFilter) => {
        setGlobalFilter(filter);
    };

    if (isLoading(device) || isLoading(deviceDetails)) {
        return null; /* TODO: Spinner, its loading */
    }

    const view = viewId ? viewStore.get(viewId) : undefined;

    if (isNotFound(device) || isNotFound(deviceDetails) || isNotFound(view)) {
        return <NotFound />;
    }

    if (deviceDetails.type !== "default") {
        return <NotFound />;
    }

    if (viewId && isLoading(view)) {
        return null; /* TODO: Spinner, its loading */
    }

    return (
        <EditorStoreContextProvider storeId={defaultStoreId}>
            <StatelessViewDevice
                deviceId={deviceId}
                device={device}
                deviceDetails={deviceDetails}
                viewId={viewId}
                view={view}
                onChangeFilter={onChangeFilter}
            />
        </EditorStoreContextProvider>
    );
});
